import React, { useState } from 'react';
import SignInDialog from './SignInDialog';

interface GlobalHeaderProps {
  user: {
    name: string;
    email: string;
    picture?: string;
  } | null;
  onSignOut: () => void;
  onSignIn: () => void;
  activeTab?: 'courses' | 'certified-testers' | 'testimonials';
}

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ user, onSignOut, onSignIn, activeTab = 'courses' }) => {
  const [isSignInDialogOpen, setIsSignInDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignInClick = () => {
    setIsSignInDialogOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleSignIn = async () => {
    await onSignIn();
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = () => {
    onSignOut();
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(229, 231, 235, 0.5)',
        transition: 'all 0.3s ease'
      }}>
        <div className="header-content" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '16px 24px'
        }}>
          {/* Logo/Brand */}
          <a
            href="#/courses"
            className="brand-logo"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = '#/courses';
              setIsMobileMenuOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              padding: '6px',
              borderRadius: '12px',
              marginLeft: '-6px',
              zIndex: 1002 // Ensure logo is above mobile menu overlay
            }}
            title="Go to homepage"
          >
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)',
            }}>
              <span style={{
                fontSize: '20px',
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}>🎓</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h1 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '800',
                background: 'linear-gradient(90deg, #111827, #4B5563)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                SDETPRO
              </h1>
            </div>
          </a>

          <style>
            {`
              @media (max-width: 768px) {
                .header-content {
                    position: relative;
                    justify-content: space-between !important;
                }
                .brand-logo {
                    position: relative;
                    left: auto;
                    transform: none;
                }
                .desktop-nav {
                  display: none !important;
                }
                .mobile-menu-btn {
                  display: flex !important;
                }
                .user-actions-desktop {
                    display: none !important;
                }
              }
              @media (min-width: 769px) {
                .mobile-menu-btn {
                  display: none !important;
                }
                .mobile-menu-overlay {
                    display: none !important;
                }
              }
            `}
          </style>

          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <a
              href="#/courses"
              className={`nav-link ${activeTab === 'courses' ? 'active' : ''}`}
              style={{
                textDecoration: 'none',
                color: activeTab === 'courses' ? '#4F46E5' : '#64748b',
                fontWeight: activeTab === 'courses' ? '700' : '500',
                fontSize: '15px',
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                backgroundColor: activeTab === 'courses' ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
              }}
            >
              Courses
            </a>
            <a
              href="#/certified-testers"
              className={`nav-link ${activeTab === 'certified-testers' ? 'active' : ''}`}
              style={{
                textDecoration: 'none',
                color: activeTab === 'certified-testers' ? '#4F46E5' : '#64748b',
                fontWeight: activeTab === 'certified-testers' ? '700' : '500',
                fontSize: '15px',
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                backgroundColor: activeTab === 'certified-testers' ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
              }}
            >
              Certified Testers
            </a>
            <a
              href="#/testimonials"
              className={`nav-link ${activeTab === 'testimonials' ? 'active' : ''}`}
              style={{
                textDecoration: 'none',
                color: activeTab === 'testimonials' ? '#4F46E5' : '#64748b',
                fontWeight: activeTab === 'testimonials' ? '700' : '500',
                fontSize: '15px',
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                backgroundColor: activeTab === 'testimonials' ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
              }}
            >
              Testimonials
            </a>
          </div>

          {/* Desktop User Actions */}
          <div className="user-actions-desktop" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            {user ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 12px 6px 6px',
                  backgroundColor: 'rgba(243, 244, 246, 0.6)',
                  borderRadius: '999px',
                  border: '1px solid rgba(229, 231, 235, 0.8)',
                }}>
                  <img
                    src={user.picture}
                    alt="Profile"
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      border: '2px solid white',
                    }}
                  />
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#374151',
                    paddingRight: '4px'
                  }}>
                    {user.name}
                  </span>
                </div>
                <button
                  onClick={onSignOut}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#EF4444',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={handleSignInClick}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            style={{
              display: 'none',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              zIndex: 1002,
              color: '#1e293b'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMobileMenuOpen ? (
                <path d="M18 6L6 18M6 6l12 12" />
              ) : (
                <path d="M3 12h18M3 6h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          zIndex: 1001,
          padding: '100px 24px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: 'none',
                color: '#1e293b',
                padding: '8px 0',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Close Menu
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <button
              onClick={() => {
                window.location.hash = '#/courses';
                setIsMobileMenuOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px',
                textAlign: 'left',
                fontSize: '18px',
                fontWeight: '600',
                color: activeTab === 'courses' ? '#4F46E5' : '#1e293b',
                cursor: 'pointer',
                borderRadius: '12px',
                backgroundColor: activeTab === 'courses' ? 'rgba(79, 70, 229, 0.05)' : 'transparent'
              }}
            >
              Courses
            </button>
            <button
              onClick={() => {
                window.location.hash = '#/certified-testers';
                setIsMobileMenuOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px',
                textAlign: 'left',
                fontSize: '18px',
                fontWeight: '600',
                color: activeTab === 'certified-testers' ? '#4F46E5' : '#1e293b',
                cursor: 'pointer',
                borderRadius: '12px',
                backgroundColor: activeTab === 'certified-testers' ? 'rgba(79, 70, 229, 0.05)' : 'transparent'
              }}
            >
              Certified Testers
            </button>
            <button
              onClick={() => {
                window.location.hash = '#/testimonials';
                setIsMobileMenuOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '12px',
                textAlign: 'left',
                fontSize: '18px',
                fontWeight: '600',
                color: activeTab === 'testimonials' ? '#4F46E5' : '#1e293b',
                cursor: 'pointer',
                borderRadius: '12px',
                backgroundColor: activeTab === 'testimonials' ? 'rgba(79, 70, 229, 0.05)' : 'transparent'
              }}
            >
              Testimonials
            </button>
          </div>

          <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '8px 0' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {user ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                }}>
                  <img
                    src={user.picture}
                    alt="Profile"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: '2px solid white',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {user.name}
                    </span>
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      {user.email}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  style={{
                    padding: '16px',
                    backgroundColor: '#FEF2F2',
                    color: '#EF4444',
                    border: '1px solid #FECACA',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={handleSignInClick}
                style={{
                  padding: '16px',
                  background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sign In Dialog */}
      <SignInDialog
        isOpen={isSignInDialogOpen}
        onClose={() => setIsSignInDialogOpen(false)}
        onSignIn={handleSignIn}
      />
    </>
  );
};

export default GlobalHeader;
